/*  MUI  */
import { Grid } from '@mui/material';
import { BreakdownCard } from 'application/Shared/Components/Cards/Components/BreakdownCard';

/*  Shared  */
import {
    DataAPIVisualsPieChart,
    DataAPIVisualsTotal
} from 'application/Shared/Components/DataAPIVisualsCard';
import { useManagedTableV2Context } from 'application/Shared/Components/Table';
import {
    ClassificationStatus,
    LifecycleStatus,
    RiskLevel
} from 'application/Shared/Constants';

/*  NPM  */
import { differenceInDays } from 'date-fns';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

const classifiedOptions = ['classified', 'unclassified'];

const ApplicationStatesCard = ({ numClassifiedApps, numUnclassifiedApps }) => {
    const { table } = useManagedTableV2Context();
    const { t } = useTranslation();

    // Extract columnFilters of the table state to reset, if necessary
    const { columnFilters } = table.getState();

    const updateFilters = ({ classifiedOption, filteredValue }) => {
        const filteredFilters = columnFilters?.filter(
            (filter) => filter.id !== 'classified'
        );
        // If we need to unselect, clear the selections in the column filter
        if (
            (classifiedOption === 'classified' &&
                filteredValue?.length > 0 &&
                filteredValue.includes(ClassificationStatus.Classified)) ||
            (classifiedOption === 'unclassified' &&
                filteredValue?.length > 0 &&
                filteredValue.includes(ClassificationStatus.Unclassified))
        ) {
            table.setColumnFilters([...filteredFilters]);
        } else {
            // else, we are selecting an option. In this case, filter values for classified can be true or false, a string, not a boolean, though.
            table.setColumnFilters([
                ...filteredFilters,
                {
                    id: 'classified',
                    value: [(classifiedOption === 'classified').toString()]
                }
            ]);
        }
    };

    const column = table.getColumn('classified');
    const filteredValue = column.getFilterValue();
    const filteredResults =
        filteredValue !== undefined && filteredValue?.length !== 0;

    const applicationData = classifiedOptions.map((classifiedOption) => ({
        id: classifiedOption,
        label: t(`Common.${classifiedOption}`),
        onClick: () => {
            updateFilters({ classifiedOption, filteredValue });
        },
        value:
            classifiedOption === 'classified'
                ? numClassifiedApps
                : numUnclassifiedApps
    }));

    return (
        <BreakdownCard
            data={applicationData}
            filteredResults={filteredResults}
            title={t('Common.applicationStates')}
        />
    );
};

export const AppInfoCards = () => {
    const { apiLoading, data, t, table } = useManagedTableV2Context();

    const currentRows = table.getFilteredRowModel().rows;
    const classifiedApps = currentRows?.filter(
        (row) => !!row.original.classified
    );
    const numClassifiedApps = classifiedApps?.length || 0;
    const numUnclassifiedApps = Number.isFinite(currentRows?.length)
        ? currentRows.length - numClassifiedApps
        : 0;

    const rightNow = new Date();

    let appsCreatedLessThan30DaysAgo = [];

    if (Array.isArray(data)) {
        appsCreatedLessThan30DaysAgo = data?.filter((app) => {
            const diffDays = differenceInDays(
                rightNow,
                new Date(app.dateCreated)
            );
            return diffDays <= 30;
        });
    }

    return (
        <Grid container spacing={3} mb="24px" data-testid="appInfoCards">
            <Grid item xs={12} sm={6} lg={3}>
                <DataAPIVisualsTotal
                    card
                    data={appsCreatedLessThan30DaysAgo}
                    loading={apiLoading}
                    title={t('ApplicationDashboard.recentlyDiscoveredApps')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <ApplicationStatesCard
                    numClassifiedApps={numClassifiedApps}
                    numUnclassifiedApps={numUnclassifiedApps}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <DataAPIVisualsPieChart
                    accessorKey="application.risk"
                    card
                    data={data}
                    loading={apiLoading}
                    isRiskChart
                    nameMap={{
                        [RiskLevel.Alert]: t('Risk.Alert'),
                        [RiskLevel.Critical]: t('Risk.Critical'),
                        [RiskLevel.High]: t('Risk.High'),
                        [RiskLevel.Low]: t('Risk.Low'),
                        [RiskLevel.Medium]: t('Risk.Medium'),
                        [RiskLevel.Safe]: t('Risk.Safe')
                    }}
                    title={t('Common.applicationRisk')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <DataAPIVisualsPieChart
                    accessorKey="status"
                    maxNumberOfPieSlicesToDisplay={8}
                    card
                    data={data}
                    loading={apiLoading}
                    nameMap={{
                        [LifecycleStatus.Approved]: t('Common.approved'),
                        [LifecycleStatus.Decommissioned]: t(
                            'Common.decommissioned'
                        ),
                        [LifecycleStatus.Decommissioning]: t(
                            'Common.decommissioning'
                        ),
                        [LifecycleStatus.Evaluating]: t('Common.evaluating'),
                        [LifecycleStatus.Implementing]: t(
                            'Common.implementing'
                        ),
                        [LifecycleStatus.NotConfigured]: t(
                            'Common.not_configured'
                        ),
                        [LifecycleStatus.Unapproved]: t('Common.unapproved')
                    }}
                    noLegend
                    title={t('Common.lifecycleStage')}
                />
            </Grid>
        </Grid>
    );
};
